import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { ChevronRight, CalendarDays, FileText, AlertTriangle, MapPin, Thermometer, X, Lock, ShieldCheck } from 'lucide-react';
import { Card, Button } from '../components';
import { MedewerkerCrisisKnop } from '../components/MedewerkerCrisisKnop';
import { DirecteHulplijnKnop } from '../components/veiligeHaven/DirecteHulplijnKnop';
import { WalletMeter } from '../components/veiligeHaven/WalletMeter';
import { MedewerkerIntro } from '../components/MedewerkerIntro';
import { getDashboard, beantwoordTegenvoorstel } from '../services/medewerkerApi';
import type { MedewerkerAfspraak, MedewerkerDashboard } from '../services/medewerkerApi';
import api from '../services/api';
import { hulplijnApi, HULPLIJN_KEY, kofferApi, KOFFER_STATUS_KEY } from '../services/veiligeHavenApi';
import { ReviewUitnodigingModal, TeBeoordelenLijst, useOpenReviewUitnodigingen } from '../components/ReviewUitnodigingModal';

function foutMelding(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const msg = (error.response?.data as { message?: string } | undefined)?.message;
    if (msg) return msg;
  }
  return fallback;
}

function afspraakTijd(iso: string): string {
  return new Date(iso).toLocaleString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** Eén van de vier vensterblokken van de startpagina (onderdeel 60, Sectie 1.1). */
const VensterBlok: React.FC<{ emoji: string; titel: string; sub: string; children: React.ReactNode; actie?: React.ReactNode }> = ({ emoji, titel, sub, children, actie }) => (
  <section className="flex flex-col rounded-2xl border border-[#E9E4D8] bg-white p-4 sm:p-5 shadow-[0_1px_2px_rgba(60,50,30,0.04)]">
    <header className="flex items-start gap-3">
      <span className="w-10 h-10 rounded-xl bg-[#F4F0E6] flex items-center justify-center text-xl shrink-0" aria-hidden="true">
        {emoji}
      </span>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[#8A7F68]">{sub}</p>
        <h2 className="font-semibold text-gray-900">{titel}</h2>
      </div>
    </header>
    <div className="mt-3 flex-1">{children}</div>
    {actie && <div className="mt-4">{actie}</div>}
  </section>
);

const VensterLink: React.FC<{ to: string; children: React.ReactNode }> = ({ to, children }) => (
  <Link to={to} className="inline-flex items-center gap-1 text-sm font-medium text-[#0B1F3A] hover:underline">
    {children}
    <ChevronRight className="w-4 h-4" aria-hidden="true" />
  </Link>
);

/** De twee domeinen van Expert Inloop, in deze volgorde. */
const EXPERT_DOMEINEN = ['vertrouwenspersoon', 'bedrijfsmaatschappelijk_werker'];

const dismissKey = (periodeLabel: string) => `thermo_dismissed_${periodeLabel}`;

function isThermoDismissed(periodeLabel: string): boolean {
  try {
    return sessionStorage.getItem(dismissKey(periodeLabel)) === '1';
  } catch {
    return false;
  }
}

function dismissThermo(periodeLabel: string): void {
  try {
    sessionStorage.setItem(dismissKey(periodeLabel), '1');
  } catch {
    /* sessionStorage niet beschikbaar — dan vragen we het volgende keer gewoon opnieuw */
  }
}

/**
 * Startpagina van het Medewerkersportaal — De Veilige Haven (onderdeel 60):
 * bovenaan de Directe Hulplijn, daaronder het 4-vensterblok (Expert Inloop,
 * Academie & POP-budget, Bedrijfsthermometer, Mijn Koffer) en de komende
 * afspraken. De chat-console zweeft rechtsonder (MedewerkerLayout).
 */
export const MedewerkerDashboardPage: React.FC = () => {
  const { t } = useTranslation('medewerker');
  const navigate = useNavigate();
  const [thermoPopup, setThermoPopup] = useState(false);

  const dashboardQuery = useQuery({ queryKey: ['medewerker-dashboard'], queryFn: getDashboard });
  const hulplijnQuery = useQuery({ queryKey: HULPLIJN_KEY, queryFn: hulplijnApi.status, retry: false });
  const kofferQuery = useQuery({ queryKey: KOFFER_STATUS_KEY, queryFn: kofferApi.status, retry: false });
  // Zelfde sleutel en vorm als MedewerkerPeilingen: open metingen van HR
  const peilingenQuery = useQuery({
    queryKey: ['medewerker-peilingen'],
    queryFn: async () => (await api.get<{ data: { id: string }[] }>('/medewerker/peilingen')).data.data,
    retry: false,
  });
  const data: MedewerkerDashboard | undefined = dashboardQuery.data;
  // Review Engine: open uitnodigingen (na voltooid gesprek/traject) → in-app modal
  const reviewsQuery = useOpenReviewUitnodigingen();
  const openReviews = reviewsQuery.data ?? [];
  const [reviewModalWeg, setReviewModalWeg] = useState<Set<string>>(new Set());
  const modalReview = openReviews.find((r) => !reviewModalWeg.has(r.id)) ?? null;

  useEffect(() => {
    if (!data) return;
    if (data.thermometer.ingevuld === false && !isThermoDismissed(data.thermometer.periode_label)) {
      setThermoPopup(true);
    }
  }, [data]);

  const sluitPopup = () => {
    if (data) dismissThermo(data.thermometer.periode_label);
    setThermoPopup(false);
  };

  if (dashboardQuery.isLoading) {
    return <div className="py-16 text-center text-gray-500">…</div>;
  }

  if (dashboardQuery.isError || !data) {
    return (
      <Card className="p-6 text-center">
        <p className="text-sm text-red-600" role="alert">
          {foutMelding(dashboardQuery.error, t('dashboard.laadfout', { defaultValue: 'Het dashboard kon niet worden geladen.' }))}
        </p>
        <Button variant="outline" className="mt-4" onClick={() => dashboardQuery.refetch()}>
          {t('algemeen.opnieuw', { defaultValue: 'Opnieuw proberen' })}
        </Button>
      </Card>
    );
  }

  const domeinen = (hulplijnQuery.data?.disciplines ?? [])
    .filter((d) => EXPERT_DOMEINEN.includes(d.professional_type))
    .sort((a, b) => EXPERT_DOMEINEN.indexOf(a.professional_type) - EXPERT_DOMEINEN.indexOf(b.professional_type));
  const openPeilingen = peilingenQuery.data?.length ?? 0;
  // Zero-State tenzij de kwartaalmeting of een HR-meting nog openstaat
  const meting = data.thermometer.ingevuld === false || openPeilingen > 0;

  const locatieLabels: Record<string, string> = {
    ONLINE: t('locatie.ONLINE', { defaultValue: 'Online' }),
    WERKGEVER: t('locatie.WERKGEVER', { defaultValue: 'Op het werk' }),
    EXTERN: t('locatie.EXTERN', { defaultValue: 'Externe locatie' }),
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {t('dashboard.hallo', { defaultValue: 'Hallo {{naam}}', naam: data.pseudoniem })}
        </h1>
        {data.bedrijfsnaam && <p className="text-sm text-gray-600 mt-0.5">{data.bedrijfsnaam}</p>}
      </div>

      <DirecteHulplijnKnop />

      <MedewerkerCrisisKnop />

      {data.te_ondertekenen > 0 && (
        <Link
          to="/medewerker/documenten"
          className="block rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-center gap-3 text-sm text-amber-900"
        >
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" aria-hidden="true" />
          <span className="flex-1">
            {t('dashboard.teOndertekenen', {
              defaultValue: 'Je hebt {{n}} document(en) te ondertekenen',
              n: data.te_ondertekenen,
            })}
          </span>
          <ChevronRight className="w-4 h-4 shrink-0" aria-hidden="true" />
        </Link>
      )}

      <MedewerkerIntro />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <VensterBlok emoji="🧑‍⚕️" sub={t('venster.expertSub', { defaultValue: 'Professional zoeken & boeken' })} titel={t('venster.expert', { defaultValue: 'Expert Inloop' })}
          actie={<VensterLink to="/medewerker/professionals">{t('venster.alleExperts', { defaultValue: 'Alle experts in je pool' })}</VensterLink>}
        >
          <p className="text-sm text-gray-600">
            {t('venster.expertUitleg', { defaultValue: 'Kies een domein en boek een vrij moment. Je professional bevestigt de afspraak; daarna kun je ook veilig chatten.' })}
          </p>
          {domeinen.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {domeinen.map((d) => (
                <Link
                  key={d.professional_type}
                  to={`/medewerker/professionals?discipline=${encodeURIComponent(d.professional_type)}`}
                  className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-900 hover:bg-emerald-100"
                >
                  {d.professional_type === 'bedrijfsmaatschappelijk_werker' ? t('venster.bmw', { defaultValue: 'BMW' }) : d.label}
                </Link>
              ))}
            </div>
          )}
          <p className="mt-3 text-xs text-gray-500">
            {t('dashboard.expertsDisciplines', {
              defaultValue: '{{experts}} experts · {{disciplines}} disciplines',
              experts: data.professionals.aantal,
              disciplines: data.professionals.disciplines,
            })}
            {data.professionals.lopend > 0 &&
              ` · ${t('dashboard.lopend', { defaultValue: '{{n}} lopend traject(en)', n: data.professionals.lopend })}`}
          </p>
        </VensterBlok>

        <VensterBlok emoji="🎓" sub={t('venster.academieSub', { defaultValue: 'Scholingsportaal & wallet' })} titel={t('venster.academie', { defaultValue: 'Academie & POP-budget' })}
          actie={
            <div className="flex flex-wrap items-center justify-between gap-2">
              <VensterLink to="/medewerker/academy">{t('venster.naarAcademy', { defaultValue: 'Naar de Academy' })}</VensterLink>
              <Link to="/medewerker/academy?helpdesk=1" className="text-xs text-gray-500 underline hover:text-gray-800">
                {t('venster.betaalprobleem', { defaultValue: 'Probleem met een betaling melden' })}
              </Link>
            </div>
          }
        >
          <WalletMeter wallet={data.academy} />
        </VensterBlok>

        <VensterBlok emoji="📊" sub={t('venster.thermometerSub', { defaultValue: 'Anonieme puls per kwartaal' })} titel={t('pijler.thermometer', { defaultValue: 'Bedrijfsthermometer' })}
          actie={meting ? <VensterLink to="/medewerker/thermometer">{t('thermometer.nuInvullen', { defaultValue: 'Nu invullen' })}</VensterLink> : undefined}
        >
          {meting ? (
            <>
              <p className="text-sm font-medium text-[#0B1F3A]">
                {openPeilingen > 0 && data.thermometer.ingevuld
                  ? t('venster.peilingKlaar', { defaultValue: 'Je werkgever heeft een meting gestart. Er staat een korte vragenlijst voor je klaar.' })
                  : t('thermometer.staatKlaar', { defaultValue: 'Staat voor je klaar (2 min)' })}
              </p>
              <p className="mt-1 text-xs text-gray-500">{data.thermometer.periode_label}</p>
            </>
          ) : (
            <p className="flex items-start gap-2 text-sm text-gray-700">
              <span className="relative mt-1.5 flex w-2.5 h-2.5 shrink-0">
                <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60 animate-ping motion-reduce:animate-none" />
                <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-green-500" />
              </span>
              <span>
                <strong className="text-green-800">{t('venster.systeemActief', { defaultValue: 'Systeem Actief' })}</strong>
                {' — '}
                {t('venster.stabiel', { defaultValue: 'Uw organisatie is momenteel stabiel. Er staan geen openstaande metingen voor u klaar.' })}
              </span>
            </p>
          )}
          <p className="mt-3 text-[11px] text-gray-500 flex items-center gap-1">
            <ShieldCheck className="w-3.5 h-3.5 text-green-600" aria-hidden="true" />
            {t('venster.thermometerAnoniem', { defaultValue: 'Je antwoorden worden los van je account opgeslagen.' })}
          </p>
        </VensterBlok>

        <VensterBlok emoji="💼" sub={t('venster.kofferSub', { defaultValue: 'Secure casus-vault' })} titel={t('koffer.titel', { defaultValue: 'Mijn Koffer' })}
          actie={<VensterLink to="/medewerker/koffer">{t('venster.openKoffer', { defaultValue: 'Open mijn koffer' })}</VensterLink>}
        >
          <ul className="space-y-1 text-sm text-gray-700">
            <li>A · {t('koffer.trajecten', { defaultValue: 'Mijn Trajecten' })}</li>
            <li>B · {t('koffer.academie', { defaultValue: 'Mijn Academie-Wallet' })}</li>
            <li>C · {t('koffer.escalatie', { defaultValue: 'Privacy & Escalatie' })}</li>
          </ul>
          <p className="mt-3 text-xs text-gray-500 flex items-center gap-1">
            <Lock className="w-3.5 h-3.5" aria-hidden="true" />
            {kofferQuery.data?.pin_ingesteld
              ? t('venster.kofferPinAan', { defaultValue: 'Verzegeld met je pincode' })
              : t('venster.kofferPinUit', { defaultValue: 'Tip: verzegel je koffer met een pincode' })}
          </p>
        </VensterBlok>
      </div>

      <TeBeoordelenLijst uitnodigingen={openReviews} />

      <section>
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5 mb-2">
          <CalendarDays className="w-4 h-4 text-gray-500" aria-hidden="true" />
          {t('dashboard.komendeAfspraken', { defaultValue: 'Komende afspraken' })}
        </h2>
        {data.komende_afspraken.length === 0 ? (
          <Card className="p-4">
            <p className="text-sm text-gray-500">{t('dashboard.geenAfspraken', { defaultValue: 'Je hebt geen afspraken gepland.' })}</p>
          </Card>
        ) : (
          <ul className="space-y-2">
            {data.komende_afspraken.map((a) => (
              <li key={a.id}>
                <Card className={`p-4 ${a.status === 'TEGENVOORSTEL' ? 'border-blue-200 bg-blue-50' : ''}`}>
                  <p className="font-medium text-gray-900">{a.professional_naam}</p>
                  <p className="text-xs text-gray-500">{a.discipline_label}</p>
                  {a.status !== 'SCHEDULED' && <AfspraakStatus afspraak={a} />}
                  <p className="text-sm text-gray-800 mt-1.5 capitalize">{afspraakTijd(a.start_tijd)}</p>
                  <p className="text-xs text-gray-600 flex items-center gap-1 mt-0.5">
                    <MapPin className="w-3.5 h-3.5" aria-hidden="true" />
                    {locatieLabels[a.locatie_type] ?? a.locatie_type}
                    {a.locatie_omschrijving ? ` — ${a.locatie_omschrijving}` : ''}
                  </p>
                  {a.status === 'TEGENVOORSTEL' && <TegenvoorstelKnoppen afspraakId={a.id} />}
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Link to="/medewerker/verslagen" className="flex items-center gap-2 text-sm text-[#0B1F3A] font-medium">
        <FileText className="w-4 h-4" aria-hidden="true" />
        {t('dashboard.verslagenLink', { defaultValue: 'Mijn gespreksverslagen' })}
        <ChevronRight className="w-4 h-4" aria-hidden="true" />
      </Link>

      <Link to="/medewerker/documenten" className="flex items-center gap-2 text-sm text-[#0B1F3A] font-medium">
        <FileText className="w-4 h-4" aria-hidden="true" />
        {t('dashboard.documentenLink', { defaultValue: 'Mijn Poortwachter-documenten' })}
        <ChevronRight className="w-4 h-4" aria-hidden="true" />
      </Link>

      {!thermoPopup && modalReview && (
        <ReviewUitnodigingModal
          uitnodiging={modalReview}
          onSluit={() => setReviewModalWeg((prev) => new Set(prev).add(modalReview.id))}
        />
      )}

      {thermoPopup && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center" onClick={sluitPopup}>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="thermo-popup-titel"
            className="w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-blue-50 p-2">
                  <Thermometer className="w-5 h-5 text-[#0B1F3A]" aria-hidden="true" />
                </span>
                <h2 id="thermo-popup-titel" className="font-semibold text-gray-900">
                  {t('thermometer.popupTitel', { defaultValue: 'De Bedrijfsthermometer staat voor u klaar.' })}
                </h2>
              </div>
              <button type="button" onClick={sluitPopup} aria-label={t('algemeen.sluiten', { defaultValue: 'Sluiten' })} className="p-1 text-gray-400 hover:text-gray-700">
                <X className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>
            <p className="text-sm text-gray-700 mt-3">
              {t('thermometer.popupTekst', { defaultValue: 'Vul in 2 minuten anoniem de scan in.' })}{' '}
              <span className="text-gray-500">({data.thermometer.periode_label})</span>
            </p>
            <div className="mt-5 flex flex-col gap-2">
              <Button
                size="lg"
                className="w-full"
                onClick={() => {
                  setThermoPopup(false);
                  navigate('/medewerker/thermometer');
                }}
              >
                {t('thermometer.nuInvullen', { defaultValue: 'Nu invullen' })}
              </Button>
              <Button variant="outline" size="lg" className="w-full" onClick={sluitPopup}>
                {t('thermometer.later', { defaultValue: 'Later' })}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MedewerkerDashboardPage;

/** Verzoek wacht op de professional, of de professional deed een tegenvoorstel. */
const AfspraakStatus: React.FC<{ afspraak: MedewerkerAfspraak }> = ({ afspraak }) => {
  const { t } = useTranslation('medewerker');
  return (
    <p className={`mt-1.5 text-xs font-semibold ${afspraak.status === 'TEGENVOORSTEL' ? 'text-blue-800' : 'text-amber-700'}`}>
      {afspraak.status === 'TEGENVOORSTEL'
        ? t('afspraak.tegenvoorstel', { defaultValue: 'Je professional stelt een ander moment voor' })
        : t('afspraak.wachtOpBevestiging', { defaultValue: 'Wacht op bevestiging van je professional' })}
    </p>
  );
};

const TegenvoorstelKnoppen: React.FC<{ afspraakId: string }> = ({ afspraakId }) => {
  const { t } = useTranslation('medewerker');
  const queryClient = useQueryClient();
  const antwoord = useMutation({
    mutationFn: (akkoord: boolean) => beantwoordTegenvoorstel(afspraakId, akkoord),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['medewerker-dashboard'] }),
  });
  return (
    <div className="mt-3">
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => antwoord.mutate(true)} disabled={antwoord.isPending}>
          {t('afspraak.accepteren', { defaultValue: 'Akkoord, plan in' })}
        </Button>
        <Button size="sm" variant="outline" onClick={() => antwoord.mutate(false)} disabled={antwoord.isPending}>
          {t('afspraak.weigeren', { defaultValue: 'Weigeren' })}
        </Button>
      </div>
      {antwoord.isError && (
        <p className="text-xs text-red-600 mt-1">{t('afspraak.antwoordMislukt', { defaultValue: 'Dat lukte niet. Probeer het opnieuw.' })}</p>
      )}
    </div>
  );
};
